import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllMarketplacePosts.graphql';
import { Query, QueryallMarketplacePostsArgs } from '../../__generated__/globalTypes';

export const useAllMarketplacePostsQuery = (options?: QueryHookOptions<Query, QueryallMarketplacePostsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallMarketplacePostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllMarketplacePostsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallMarketplacePostsArgs>
): QueryResult<Query, QueryallMarketplacePostsArgs> & {
  getAllMarketplacePosts: LazyQueryExecFunction<Query, QueryallMarketplacePostsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllMarketplacePosts, result] = useLazyQuery<Query, QueryallMarketplacePostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllMarketplacePosts, ...result };
};

export const useAllMarketplacePostsPromiseQuery = () => {
  const client = useApolloClient();

  const getAllMarketplacePosts = useCallback(
    (options?: Omit<QueryOptions<QueryallMarketplacePostsArgs, Query>, 'query'>) =>
      client.query<Query, QueryallMarketplacePostsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllMarketplacePosts };
};
// Suspense Query Hooks
export const useAllMarketplacePostsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallMarketplacePostsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallMarketplacePostsArgs>(QUERY, {
    ...options
  });

  return result;
};
